import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Phone, Mail, Building2, Cpu, DollarSign, Clock, FileCheck } from "lucide-react";

const FIELDS = [
  { icon: Building2, k: "COMPANY", v: "Fortune 500 Retail Group (Anonymized)", ok: true },
  { icon: Phone, k: "DIRECT DIAL", v: "+1 (•••) •••-4217 — switchboard verified", ok: true },
  { icon: Mail, k: "CORPORATE EMAIL", v: "j.m•••@••••••.com — SMTP handshake passed", ok: true },
  { icon: Cpu, k: "TECH STACK INSTALLED", v: "AWS · ServiceNow · Legacy SAP ECC (expiring Q4)", ok: false },
  { icon: DollarSign, k: "REVENUE BRACKET", v: "$1B – $5B · 12,400 employees", ok: false },
  { icon: FileCheck, k: "SOURCE ASSET", v: '"2026 Cloud Migration Benchmark Report"', ok: false },
  { icon: Clock, k: "OPT-IN PROOF", v: "2026-06-18 14:32 UTC · IP verified · GDPR trail attached", ok: true },
];

export const LeadDrawer = ({ buttonLabel = "View Anonymized Sample Lead Record", buttonClass = "" }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        data-testid="lead-drawer-trigger"
        onClick={() => setOpen(true)}
        className={
          buttonClass ||
          "group inline-flex items-center gap-2 rounded-full border border-cobalt px-6 py-3 text-sm font-semibold text-cobalt transition-all duration-300 hover:bg-cobalt hover:text-white"
        }
      >
        <FileCheck className="h-4 w-4" />
        {buttonLabel}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-navy/60 backdrop-blur-sm"
              data-testid="lead-drawer-backdrop"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className="fixed inset-y-0 right-0 z-[70] w-full max-w-md overflow-y-auto border-l border-line bg-white shadow-2xl"
              data-testid="lead-drawer-panel"
              data-lenis-prevent
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-navy px-6 py-4">
                <div>
                  <p className="font-mono2 text-[9px] tracking-[0.24em] text-cobaltlight">CRM RECORD PREVIEW · HQL</p>
                  <p className="font-jakarta text-lg font-bold text-white">Sample Lead Record</p>
                </div>
                <button
                  data-testid="lead-drawer-close"
                  onClick={() => setOpen(false)}
                  className="grid h-9 w-9 place-items-center rounded-lg border border-white/15 text-white transition-colors hover:bg-white/10"
                  aria-label="Close sample lead"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-4 rounded-2xl border border-line bg-canvas p-5">
                  <span className="grid h-14 w-14 place-items-center rounded-xl bg-eblue font-jakarta text-xl font-extrabold text-white">
                    JM
                  </span>
                  <div>
                    <p className="font-jakarta text-lg font-bold text-navy">J. M———</p>
                    <p className="text-sm text-slate-500">VP of IT Infrastructure</p>
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-600">
                      <ShieldCheck className="h-3 w-3" /> Triple-Verified
                    </span>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {FIELDS.map((f, i) => (
                    <motion.div
                      key={f.k}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                      className="flex items-start gap-3 rounded-xl border border-line p-4"
                    >
                      <f.icon className="mt-0.5 h-4 w-4 shrink-0 text-cobalt" />
                      <div className="min-w-0">
                        <p className="font-mono2 text-[9px] tracking-[0.2em] text-slate-400">{f.k}</p>
                        <p className="mt-0.5 break-words text-sm font-medium text-charcoal">{f.v}</p>
                      </div>
                      {f.ok && <ShieldCheck className="ml-auto h-4 w-4 shrink-0 text-emerald-500" />}
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 rounded-xl bg-navy p-5 text-white">
                  <p className="font-mono2 text-[9px] tracking-[0.24em] text-flame">QUALIFICATION NOTES</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    Confirmed active migration project · Budget allocated for FY26 · Decision authority shared with CIO
                    office · Timeline: 2 quarters.
                  </p>
                </div>
                <p className="mt-4 text-center text-[11px] text-slate-400">
                  Anonymized specimen. Every delivery includes full consented contact data.
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
